import React from "react";
import CaseStudy from "../case/CaseStudy";
import { Section, Figure, Stage, Grid } from "../case/blocks";
import { useStageParallax } from "../case/motion";
import "../case/case.scss";
import "./mk2.scss";

import MK2Cluster from "../../../images/mk2-cluster.png";
import MK2Application from "../../../images/mk2-hero.png";
import MK2About from "../../../images/mk2-about.png";
import MK2LiveUpdates from "../../../images/mk2-live-updates.png";
import Flowchart from "../../../images/Flowchart.png";
import LoginScreen from "../../../images/LoginScreen.png";
import CompanyListing from "../../../images/CompanyListing.png";
import ZonesListing from "../../../images/ZonesListing.png";
import RoomsDetails from "../../../images/RoomDetails.png";
import DeviceDetails from "../../../images/DevicesDetails.png";
import SchedulingDevices from "../../../images/SchedulingDevices.png";
import Client1 from "../../../images/Clients-1.png";
import Client2 from "../../../images/Clients-2.png";
import Client3 from "../../../images/Clients-3.png";
import Client4 from "../../../images/Clients-4.png";
import MK2DeviceDetails from "../../../images/mk2-device.png";
import MK2Service from "../../../images/mk2-service.png";
import MK2Billing from "../../../images/mk2-billing.png";
import MK2ZoneDetails from "../../../images/mk2-zone-details.png";

const WIREFRAMES = [
  { src: LoginScreen, label: "Login Screen" },
  { src: CompanyListing, label: "Company listing" },
  { src: ZonesListing, label: "Zones listing" },
  { src: RoomsDetails, label: "Room details" },
  { src: DeviceDetails, label: "Devices details" },
  { src: SchedulingDevices, label: "Scheduling of Devices" },
];

const TYPEFACES = [
  { name: "Articulat CF", className: "mk2-font--heading" },
  { name: "Neue Haas Grotesk Display Pro", className: "mk2-font--body" },
];

// Each inner array is one column, lined up under the specimen cards above.
const PALETTE = [
  [
    { label: "Main", colours: ["#F8F8FC"] },
    { label: "Accent", colours: ["#E3E3E3", "#FFFFFF"] },
  ],
  [{ label: "Other", colours: ["#292929"] }],
];

// One section rather than four near-identical splits in a row.
const SCREENS = [
  {
    src: MK2DeviceDetails,
    title: "Device Details",
    body: "Details and updates about the device, as well as a list of the devices in the specified zone.",
  },
  {
    src: MK2Service,
    title: "Service Mode",
    body: "Accessible to the admin. It allows the admin to test the device during maintenance.",
  },
  {
    src: MK2ZoneDetails,
    title: "Zone Details",
    body: "Zone level readings, with the devices assigned to that zone.",
    blend: true,
  },
  {
    src: MK2Cluster,
    title: "Cluster & Device Insights",
    body: "Daily insights about a single device, as well as a cluster of devices.",
  },
  {
    src: MK2Billing,
    title: "Billing & Maintenance",
    body: "Billing and maintenance details for the user and the admin to receive updates about payments and maintenance.",
  },
];

const MK2 = () => {
  useStageParallax();

  return (
    <CaseStudy
      slug="/mk2"
      title="MK2 Application"
      tagline="Fulltime Project"
      client={{ name: "Praan", href: "https://www.praan.io" }}
      roles={["User experience", "UI design", "Design system"]}
      hero={{
        src: MK2Application,
        alt: "MK2 zone dashboard showing air quality readings on a desktop monitor",
        blend: true,
      }}
      lead={
        <p>
          The MK2 application is built to monitor the air in industrial sites
          and zones where the device would be installed.
        </p>
      }
    >
      <Section id="about" title="About project">
        <div className="mk2-about" data-reveal>
          <img
            src={MK2About}
            alt="MK2 application screens laid out around a laptop"
            loading="lazy"
            decoding="async"
          />
          <p>
            MK2 application is designed for users who buy the product and
            require a way to control the device and monitor the zones
            surrounding the device. The application allows the user to monitor
            the AQI levels, CO2, NO2, SO2, O2, Temperature, Humidity,
            Particulate matter levels and Ambient sound levels.
          </p>
        </div>
      </Section>

      <Section id="flow">
        <header className="mk2-flow-head" data-reveal>
          <h2 className="cs-h2" id="flow-h">
            User
            <br />
            Flow
          </h2>
          <p>
            The following is the basic user flow of the web application, which
            includes the Admin as well as the Client side.
          </p>
        </header>
        {/* The flowchart is dark line-work on a transparent ground, so its
            connectors vanish against the page. It gets its own light artboard,
            the way it was drawn. */}
        <figure className="mk2-board" data-reveal>
          <img
            src={Flowchart}
            alt="User flow diagram covering the admin and client paths through the application"
            loading="lazy"
            decoding="async"
          />
        </figure>
      </Section>

      <Section
        id="wireframes"
        title="Wireframes"
        lead="Basic wireframes of the web application."
      >
        <div className="mk2-wires">
          {WIREFRAMES.map((w) => (
            <figure key={w.label}>
              <img src={w.src} alt={`${w.label} wireframe`} loading="lazy" />
              <figcaption className="u-mono">{w.label}</figcaption>
            </figure>
          ))}
        </div>
      </Section>

      <Section
        id="type"
        title="Fonts & colours"
        lead="The font styles and colours used for the MK2 web application."
      >
        <div className="mk2-fonts" data-reveal>
          {TYPEFACES.map((t) => (
            <div key={t.name} className={`mk2-font ${t.className}`}>
              <p className="mk2-font__kind">Sans serif</p>
              <p className="mk2-font__name">{t.name}</p>
              <ul className="mk2-font__weights">
                <li>bold</li>
                <li>black</li>
              </ul>
              <p className="mk2-font__sample" aria-hidden="true">
                Aa
              </p>
            </div>
          ))}
        </div>

        <div className="mk2-palette" data-reveal>
          {PALETTE.map((column, i) => (
            <div key={i} className="mk2-palette__col">
              {column.map((group) => (
                <div key={group.label}>
                  <p className="mk2-palette__label">{group.label}</p>
                  <ul>
                    {group.colours.map((hex) => (
                      <li key={hex}>
                        <span
                          className="mk2-swatch"
                          style={{ background: hex }}
                          aria-hidden="true"
                        />
                        <span className="mk2-swatch__hex">{hex}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="clients"
        title="Clients & Zones"
        lead="Client listings and zone listings with the current AQI and the devices assigned."
      >
        <Grid min={320}>
          <Figure src={Client1} alt="Client listing screen" />
          <Figure src={Client2} alt="Client detail screen" />
          <Figure src={Client3} alt="Zones listing screen with AQI readings" />
          <Figure src={Client4} alt="Zone detail screen with device readings" />
        </Grid>
      </Section>

      <Section id="screens" title="The screens">
        <div className="mk2-screens">
          {SCREENS.map((s) => (
            <article key={s.title} data-reveal>
              <img
                src={s.src}
                alt={`${s.title} screen`}
                loading="lazy"
                className={s.blend ? "mk2-blend" : undefined}
              />
              <div>
                <h3 className="cs-h3">{s.title}</h3>
                <p>{s.body}</p>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Stage
        src={MK2LiveUpdates}
        alt="Live air quality updates in the MK2 application"
        title="Live updates from the factory floor."
        height="96dvh"
      >
        <p>
          The application gives you live updates about the air quality around
          the factory.
        </p>
      </Stage>
    </CaseStudy>
  );
};

export default MK2;
